import * as React from 'react';

import { DimeComboboxItem } from '../DimeCombobox.js';
import { DisabledTooltip } from '../DisabledTooltip.js';
import type { DimeAutocompleteOption } from '../lib/dime-autocomplete-option.js';
import { SELECT_ALL_OPTION } from '../lib/select-all-option.js';

// Rules Select All off from the options without a separator element, which a listbox may not contain.
const SELECT_ALL_ROW =
  'mb-1 after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:bg-border';

type DimeAutocompleteOptionRowProps = Readonly<{
  option: DimeAutocompleteOption;
}>;

/** One row of a `DimeAutocomplete` list: Select All, or an option with its icon, description and disabled reason. */
export function DimeAutocompleteOptionRow({ option }: DimeAutocompleteOptionRowProps) {
  // Not built from the option's value, which may hold spaces (a contact's name) or repeat across rows.
  const rowId = React.useId();
  if (option.value === SELECT_ALL_OPTION.value) {
    return (
      <DimeComboboxItem value={option} className={SELECT_ALL_ROW}>
        {option.label}
      </DimeComboboxItem>
    );
  }
  const descriptionId = option.description ? `${rowId}-description` : undefined;
  const reasonId = `${rowId}-disabled-reason`;
  const disabledReason = option.disabled === true ? option.disabledReason : undefined;
  const describedBy = [descriptionId, disabledReason ? reasonId : undefined]
    .filter(Boolean)
    .join(' ');
  const optionItem = (
    <DimeComboboxItem
      value={option}
      disabled={option.disabled === true}
      aria-describedby={describedBy || undefined}
    >
      <span className="flex items-center gap-2">
        {option.icon && (
          <span className="shrink-0 text-muted-foreground" aria-hidden="true">
            {option.icon}
          </span>
        )}
        {option.description ? (
          <span className="flex flex-col">
            <span>{option.label}</span>
            <span id={descriptionId} className="text-xs text-muted-foreground">
              {option.description}
            </span>
          </span>
        ) : (
          option.label
        )}
      </span>
    </DimeComboboxItem>
  );
  if (!disabledReason) return optionItem;
  return (
    <>
      <DisabledTooltip tooltip={disabledReason} side="right" focusable={false} className="block">
        {optionItem}
      </DisabledTooltip>
      <span id={reasonId} hidden>
        {disabledReason}
      </span>
    </>
  );
}
